// client/src/pages/SettingsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const USERS_API_URL = `${API_ROOT_URL}/users`;   // for user-specific actions
const DEALERS_API_URL = `${API_ROOT_URL}/dealers`; // Existing endpoint for dealer actions

function SettingsPage() {
  const { user, token, isAuthenticated, login, updateUserInContext } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submissionMessage, setSubmissionMessage] = useState({ message: '', type: '' });

  // User form states
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Inline validation error states (User)
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');

  // Dealer info (read-only)
  const [dealerInfo, setDealerInfo] = useState({
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    },
    shippingPreferences: '',
  });
  const [loadingDealerInfo, setLoadingDealerInfo] = useState(false);

  // Memoized email regex (for user email validation)
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, []);

  // Fetch user data on component mount
  useEffect(() => {
    if (isAuthenticated && user) {
      setUsername(user.username);
      setEmail(user.email);
      setLoading(false); // Initial user data loaded
    } else {
      setError('User not authenticated. Please log in.');
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Fetch dealer-specific data if the user is a Dealer
  useEffect(() => {
    const fetchDealerData = async () => {
      if (isAuthenticated && user && user.role === 'Dealer' && user.associatedDealerId) {
        setLoadingDealerInfo(true);
        try {
          const response = await fetch(`${DEALERS_API_URL}/${user.associatedDealerId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          setDealerInfo({
            contactPerson: data.contactPerson || '',
            contactEmail: data.contactEmail || '',
            contactPhone: data.contactPhone || '',
            address: {
              street: data.address?.street || '',
              city: data.address?.city || '',
              state: data.address?.state || '',
              zip: data.address?.zip || '',
              country: data.address?.country || ''
            },
            shippingPreferences: data.shippingPreferences || '',
          });
        } catch (err) {
          console.error('Error fetching dealer info:', err);
          setSubmissionMessage({ message: 'Failed to load dealer information.', type: 'danger' });
        } finally {
          setLoadingDealerInfo(false);
        }
      }
    };
    fetchDealerData();
  }, [isAuthenticated, user, token]);

  // ---- Inline validation handlers (User) ----
  const validateUsername = (value) => {
    if (value.trim().length < 3) {
      setUsernameError('Username must be at least 3 characters long.');
      return false;
    }
    setUsernameError('');
    return true;
  };

  const validateEmail = (value) => {
    const v = value.trim();
    if (!emailRegex.test(v)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateNewPassword = (value) => {
    if (value && value.length < 6) {
      setNewPasswordError('New password must be at least 6 characters long.');
      return false;
    }
    setNewPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value) => {
    if (value && value !== newPassword) {
      setConfirmNewPasswordError('New passwords do not match.');
      return false;
    }
    setConfirmNewPasswordError('');
    return true;
  };

  // Handle user info update submission
  const handleUserUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmissionMessage({ message: '', type: '' });

    // Re-validate all fields on submit
    const isUsernameValid = validateUsername(username);
    const isEmailValid = validateEmail(email);
    const isNewPasswordValid = validateNewPassword(newPassword);
    const isConfirmPasswordValid = validateConfirmPassword(confirmNewPassword);

    if (!isUsernameValid || !isEmailValid || !isNewPasswordValid || !isConfirmPasswordValid) {
      setSubmissionMessage({ message: 'Please correct the errors in the form.', type: 'danger' });
      setLoading(false);
      return;
    }

    const updates = { username: username.trim(), email: email.trim() };
    if (newPassword) {
      updates.currentPassword = currentPassword;
      updates.newPassword = newPassword;
    }

    try {
      const response = await fetch(`${USERS_API_URL}/${user._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmissionMessage({ message: 'User information updated successfully!', type: 'success' });
        if (newPassword) {
          await login(email.trim(), newPassword);
        } else {
          updateUserInContext({ username: result.username, email: result.email });
        }
        // Clear password fields and their errors
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setNewPasswordError('');
        setConfirmNewPasswordError('');
      } else {
        setSubmissionMessage({ message: result.message || 'Failed to update user information.', type: 'danger' });
      }
    } catch (err) {
      console.error('Error updating user info:', err);
      setSubmissionMessage({ message: 'Network error or server unreachable. Failed to update user info.', type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading settings...</span>
        </Spinner>
        <p className="mt-2">Loading settings...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Settings</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      fluid
      style={{
        backgroundColor: '#4595ad29',
        minHeight: '100vh',
        paddingTop: '2rem',
        paddingBottom: '2rem',
        margin: 0
      }}
    >
      <Container className="my-5">
        <h2 className="mb-4 text-center">User Settings</h2>

        {submissionMessage.message && (
          <Alert variant={submissionMessage.type} className="my-3 text-center">
            {submissionMessage.message}
          </Alert>
        )}

        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow-lg p-4 mb-4">
              <Card.Body>
                <Card.Title className="mb-3">Update Personal Information</Card.Title>
                <Form onSubmit={handleUserUpdate} noValidate>
                  <Form.Group className="mb-3" controlId="formUsername">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        validateUsername(e.target.value);
                      }}
                      isInvalid={!!usernameError}
                      required
                      minLength={3}
                      disabled={loading}
                    />
                    <Form.Control.Feedback type="invalid">
                      {usernameError}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Login Email address</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        validateEmail(e.target.value);
                      }}
                      isInvalid={!!emailError}
                      required
                      disabled={loading}
                      pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
                      title="Enter a valid email address (e.g., name@example.com)"
                    />
                    <Form.Control.Feedback type="invalid">
                      {emailError}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <hr />
                  <Card.Title className="mb-3 mt-4">Change Password</Card.Title>
                    <Form.Group className="mb-3" controlId="formCurrentPassword">
                      <Form.Label>Current Password (Required if changing password)</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                          disabled={loading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowCurrentPassword(prev => !prev)}
                          disabled={loading}
                        >
                          {showCurrentPassword ? 'Hide' : 'Show'}
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  <Form.Group className="mb-3" controlId="formNewPassword">
                    <Form.Label>New Password (min 6 characters)</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          validateNewPassword(e.target.value);
                          validateConfirmPassword(confirmNewPassword); // re-validate confirm field
                        }}
                        isInvalid={!!newPasswordError}
                        placeholder="Enter new password"
                        disabled={loading}
                        minLength={6}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowNewPassword(prev => !prev)}
                        disabled={loading}
                      >
                        {showNewPassword ? 'Hide' : 'Show'}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {newPasswordError}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formConfirmNewPassword">
                    <Form.Label>Confirm New Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        value={confirmNewPassword}
                        onChange={(e) => {
                          setConfirmNewPassword(e.target.value);
                          validateConfirmPassword(e.target.value);
                        }}
                        isInvalid={!!confirmNewPasswordError}
                        placeholder="Confirm new password"
                        disabled={loading}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowConfirmNewPassword(prev => !prev)}
                        disabled={loading}
                      >
                        {showConfirmNewPassword ? 'Hide' : 'Show'}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {confirmNewPasswordError}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <Button variant="primary" type="submit" disabled={loading} className="w-100">
                    {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Update Personal Info'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {user?.role === 'Dealer' && (
          <Row className="justify-content-center mt-5">
            <Col md={8}>
              <Card className="shadow-lg p-4">
                <Card.Body>
                  <Card.Title className="mb-3">Dealer Information</Card.Title>
                  {loadingDealerInfo ? (
                    <div className="text-center">
                      <Spinner animation="border" size="sm" /> Loading Dealer Info...
                    </div>
                  ) : (
                    // Read-only dealer info: inputs are disabled and there is no submit button
                    <Form noValidate>
                      <Form.Text muted>(Read-only)</Form.Text>
                      <Form.Group className="mt-3 mb-3" controlId="formContactPerson">
                        <Form.Label>Contact Person</Form.Label>
                        <Form.Control
                          type="text"
                          name="contactPerson"
                          value={dealerInfo.contactPerson}
                          disabled
                        />
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="formContactEmail">
                        <Form.Label>Contact Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="contactEmail"
                          value={dealerInfo.contactEmail}
                          disabled
                        />
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="formContactPhone">
                        <Form.Label>Contact Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="contactPhone"
                          value={dealerInfo.contactPhone}
                          disabled
                          inputMode="tel"
                        />
                      </Form.Group>

                      <h6 className="mt-4 mb-3">Address</h6>
                      <Form.Group className="mb-3" controlId="formAddressStreet">
                        <Form.Label>Street</Form.Label>
                        <Form.Control
                          type="text"
                          name="street"
                          value={dealerInfo.address.street}
                          disabled
                        />
                      </Form.Group>
                      <Row className="mb-3">
                        <Form.Group as={Col} controlId="formAddressCity">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            name="city"
                            value={dealerInfo.address.city}
                            disabled
                          />
                        </Form.Group>
                        <Form.Group as={Col} controlId="formAddressState">
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            name="state"
                            value={dealerInfo.address.state}
                            disabled
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-3">
                        <Form.Group as={Col} controlId="formAddressZip">
                          <Form.Label>Zip Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="zip"
                            value={dealerInfo.address.zip}
                            disabled
                          />
                        </Form.Group>
                        <Form.Group as={Col} controlId="formAddressCountry">
                          <Form.Label>Country</Form.Label>
                          <Form.Control
                            type="text"
                            name="country"
                            value={dealerInfo.address.country}
                            disabled
                            autoComplete='off'
                          />
                        </Form.Group>
                      </Row>

                      <Form.Group className="mb-3" controlId="formShippingPreferences">
                        <Form.Label>Shipping Preferences</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="shippingPreferences"
                          value={dealerInfo.shippingPreferences}
                          disabled
                        />
                      </Form.Group>
                      {/* Button intentionally removed to make the section view-only */}
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </Container>
  );
}

export default SettingsPage;
